import React, { memo, useState } from "react"
import useTranslator from "@pma/hooks/use-translator"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/common/dialog"
import { Tabs, TabsList, TabsTrigger } from "@pma/components/common/work-diaries/ui/tabs"
import { ResourcesStep } from "@pma/components/common/work-diaries/work-log-form/resources-step"
import { TaskStep } from "@pma/components/common/work-diaries/work-log-form/task-step"
import { DictType } from "@pma/types"
import { CommentsStep } from "@pma/components/common/work-diaries/work-log-form/comments-step"
import { GeneralStep } from "@pma/components/common/work-diaries/work-log-form/general-step"
import PreviewDiary from "@pma/components/common/work-diaries/work-log-form/preview-diary"
import { cn } from "@pma/utils/helpers"

interface NewTaskProps {
  taskModal: boolean
  setTaskModal: React.Dispatch<React.SetStateAction<boolean>>
  workDiaries?: DictType
  project?: DictType
  lstStatus: any[]
  lstWeather: any[]
  lstStructures: any[]
  lstWorkforce: any[]
  lstTools: any[]
  lstContractor: any[]
  refetchWorkDiary: () => void
  setDiaryId?: React.Dispatch<React.SetStateAction<number>>
}

const STEPS = [
  { id: "general", label: "General" },
  { id: "resources", label: "Resources" },
  { id: "tasks", label: "Tasks" },
  { id: "comments", label: "Comments & Files" },
  { id: "preview", label: "Preview" },
]

const NewWorkDiariesComponent = ({
  lstContractor,
  lstStatus,
  lstStructures,
  lstTools,
  lstWorkforce,
  lstWeather,
  taskModal,
  setTaskModal,
  workDiaries,
  project,
  refetchWorkDiary,
  setDiaryId,
}: NewTaskProps) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const [activeStep, setActiveStep] = useState(0)

  // handle reset state
  const handleResetState = () => {
    setTaskModal(!taskModal)
  }

  return (
    <Dialog open={taskModal} onOpenChange={handleResetState}>
      <DialogContent className={cn('z-50 w-full max-w-[95vw] sm:max-w-[700px] md:max-w-[1000px]', isHebrew ? "rtl":"ltr")}>
        <DialogHeader>
          <DialogTitle className={cn('text-lg sm:text-xl', isHebrew ? "text-right":"text-left")}>
            {workDiaries ? translate("Edit Work Log") : translate("Create New Work Log")}
          </DialogTitle>
        </DialogHeader>

        <div
          className={`h-[80vh] overflow-auto px-4 py-4 sm:px-6 sm:py-6 ${isHebrew ? "rtl" : "ltr"}`}
        >
          <Tabs activeTab={activeStep}>
            <TabsList className='flex flex-wrap gap-2 sm:flex-nowrap'>
              {STEPS.map((step, index) => (
                <TabsTrigger
                  key={step.id}
                  active={activeStep === index}
                  onClick={() => setActiveStep(index)}
                  className={`px-2 py-1 text-xs sm:text-sm ${activeStep === index ? "cursor-pointer" : workDiaries?.id ? "cursor-pointer" : "cursor-not-allowed"}`}
                  disabled={!workDiaries?.id}
                >
                  {step.id == "comments"
                    ? `${translate("Comments")} & ${translate("Files")}`
                    : translate(step.label)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Tab Content */}
          <div className='mt-4 sm:mt-6'>
            {activeStep === 0 && (
              <GeneralStep
                workDiaries={workDiaries}
                project={project}
                lstStatus={lstStatus}
                lstWeather={lstWeather}
                lstContractor={lstContractor}
                setActiveStep={setActiveStep}
                setDiaryId={setDiaryId}
              />
            )}
            {activeStep === 1 && (
              <ResourcesStep
                workDiaries={workDiaries}
                project={project}
                lstStructures={lstStructures}
                lstWorkforce={lstWorkforce}
                lstTools={lstTools}
                refetchWorkDiary={refetchWorkDiary}
                setActiveStep={setActiveStep}
              />
            )}
            {activeStep === 2 && (
              <TaskStep workDiaries={workDiaries} project={project} setActiveStep={setActiveStep} />
            )}
            {activeStep === 3 && (
              <CommentsStep
                workDiaries={workDiaries}
                project={project}
                setActiveStep={setActiveStep}
              />
            )}
            {activeStep === 4 && (
              <PreviewDiary
                setTaskModal={setTaskModal}
                workDiaries={workDiaries}
                project={project}
                setActiveStep={setActiveStep}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const NewWorkDiaries = memo(NewWorkDiariesComponent)
